/**
 * The hero's try-the-parser demo (Views/Home/Index.cshtml). Progressive enhancement over the
 * server-rendered example: without JS the window stays an honest static mock (readonly textarea,
 * pre-rendered rows); with JS the textarea unlocks and every edit re-parses through the real
 * stage-1 parser at /api/syllabus-demo.
 *
 * Deliberately standalone - no imports from src/app/ (webpack's appShared cache group would
 * extract them into a bundle this page never loads, and that client's 401 handling redirects to
 * sign-in - both wrong here; see src/tools/lib/account-sync.js for the original write-up of the
 * same rule). The fetch is inlined for the same reason.
 *
 * Analytics discipline (docs/ANALYTICS.md): never the pasted text - engagement fires once with a
 * coarse trigger, and results fire at most twice a page (first zero-row parse, first non-empty
 * parse) carrying only a bucketed count and a yes/no.
 */
import { trackEvent } from './analytics.js';
import { parsedCountBucket } from './analytics-buckets.js';

const DEBOUNCE_MS = 500;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const EMPTY_INPUT_MESSAGE = 'Paste a few lines from any syllabus — the schedule and grading sections work best.';
const ZERO_FOUND_MESSAGE = 'Nothing parsed from that yet — the parser wants a date plus a word like “due”, “exam”, or “memo” on the line, and it declines to guess.';
const ERROR_MESSAGE = 'The demo hit a snag — try again in a moment.';

/** "2026-09-24" -> "Sep 24". String math on purpose: new Date('2026-09-24') is parsed as UTC
 * midnight and renders as the previous day in negative-offset timezones. */
function formatDueDate(isoDate) {
  const parts = String(isoDate || '').split('-');
  if (parts.length !== 3) return null;
  const month = MONTHS[Number(parts[1]) - 1];
  const day = Number(parts[2]);
  if (!month || !day) return null;
  return month + ' ' + day;
}

function buildRow(item, maxWeight) {
  const li = document.createElement('li');
  li.className = 'parse-item';

  const main = document.createElement('span');
  main.className = 'parse-item-main';

  const title = document.createElement('span');
  title.className = 'parse-item-title';
  title.textContent = item.title || 'Untitled';

  const meta = document.createElement('span');
  meta.className = 'parse-item-date';
  const metaParts = [formatDueDate(item.dueDate) || 'no date yet'];
  if (item.kind) metaParts.push(item.kind);
  meta.textContent = metaParts.join(' · ');
  if (item.team) {
    meta.append(' · ');
    const team = document.createElement('span');
    team.className = 'demo-team';
    team.textContent = 'Team';
    meta.append(team);
  }

  main.append(title, meta);
  li.append(main);

  if (item.weightPercent !== null && item.weightPercent !== undefined) {
    const weight = document.createElement('span');
    // Only the heaviest item reads hot - that emphasis is the point of extracting weight at
    // all, and painting every pill orange would say nothing.
    const heavy = item.weightPercent >= 20 && item.weightPercent === maxWeight;
    weight.className = heavy ? 'parse-weight parse-weight--heavy' : 'parse-weight';
    weight.textContent = String(item.weightPercent) + '%';
    li.append(weight);
  }

  return li;
}

export function initSyllabusDemo() {
  const root = document.querySelector('[data-parser-demo]');
  if (!root) return;

  const endpoint = root.getAttribute('data-demo-endpoint');
  const input = root.querySelector('[data-demo-input]');
  const tools = root.querySelector('[data-demo-tools]');
  const results = root.querySelector('[data-demo-results]');
  const empty = root.querySelector('[data-demo-empty]');
  const status = root.querySelector('[data-demo-status]');
  if (!endpoint || !input || !results || !empty || !status) return;

  // The server-rendered textarea content IS the example - one source of truth, no duplicate
  // string to drift from the controller's ExampleText.
  const exampleText = input.value;

  input.removeAttribute('readonly');
  if (tools) tools.hidden = false;

  let timer = null;
  let inflight = null;
  let lastParsed = exampleText;
  let engagedSent = false;
  let zeroResultSent = false;
  let foundResultSent = false;

  function engage(trigger) {
    if (engagedSent) return;
    engagedSent = true;
    trackEvent('parser_demo_engaged', { trigger });
  }

  function setBusy(busy) {
    results.classList.toggle('is-parsing', busy);
    empty.classList.toggle('is-parsing', busy);
  }

  function setStatus(foundCount) {
    // "parsed on our server, never stored" rather than the older "nothing you type is saved":
    // both are true about storage, but the old wording reads as "this never leaves my browser",
    // which is false here and is the promise the /tools pages actually make. Keep this and the
    // static copy in Views/Home/Index.cshtml saying the same thing as Privacy section 3.1.
    status.textContent = foundCount === null
      ? 'parsed on our server, never stored'
      : foundCount + ' deliverable' + (foundCount === 1 ? '' : 's') + ' found · parsed on our server, never stored';
  }

  function showEmpty(message) {
    results.hidden = true;
    empty.hidden = false;
    empty.textContent = message;
  }

  function showRows(items) {
    const maxWeight = items.reduce(function (max, item) {
      return Math.max(max, item.weightPercent || 0);
    }, 0);
    results.textContent = '';
    items.forEach(function (item) {
      results.append(buildRow(item, maxWeight));
    });
    results.hidden = false;
    empty.hidden = true;
  }

  function recordResult(foundCount, weightedCount) {
    if (foundCount === 0 && !zeroResultSent) {
      zeroResultSent = true;
      trackEvent('parser_demo_result', { found_count_bucket: parsedCountBucket(0), weighted: 'no' });
    } else if (foundCount > 0 && !foundResultSent) {
      foundResultSent = true;
      trackEvent('parser_demo_result', {
        found_count_bucket: parsedCountBucket(foundCount),
        weighted: weightedCount > 0 ? 'yes' : 'no',
      });
    }
  }

  async function parseNow(text) {
    if (inflight) inflight.abort();
    const controller = new AbortController();
    inflight = controller;
    setBusy(true);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text }),
        signal: controller.signal,
      });

      let payload = null;
      try { payload = await response.json(); } catch { payload = null; }

      if (!response.ok) {
        showEmpty((payload && payload.message) || ERROR_MESSAGE);
        setStatus(null);
        // Forget this text so the "try again" the message promises actually works - with
        // lastParsed kept, retyping the same line (or re-clicking the example) would be
        // deduplicated and never retry.
        lastParsed = null;
        return;
      }

      const items = (payload && payload.items) || [];
      const foundCount = (payload && payload.foundCount) || 0;
      recordResult(foundCount, (payload && payload.weightedCount) || 0);

      if (foundCount === 0) {
        showEmpty(ZERO_FOUND_MESSAGE);
      } else {
        showRows(items);
      }
      setStatus(foundCount);
    } catch (err) {
      if (err && err.name === 'AbortError') return; // superseded by a newer edit
      showEmpty(ERROR_MESSAGE);
      setStatus(null);
      lastParsed = null; // same retry reasoning as the non-OK branch above
    } finally {
      if (inflight === controller) {
        inflight = null;
        setBusy(false);
      }
    }
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    const text = input.value;

    if (text.trim().length === 0) {
      if (inflight) inflight.abort();
      showEmpty(EMPTY_INPUT_MESSAGE);
      setStatus(null);
      lastParsed = text;
      return;
    }

    if (text === lastParsed) return;

    timer = setTimeout(function () {
      lastParsed = text;
      parseNow(text);
    }, DEBOUNCE_MS);
  }

  input.addEventListener('input', function () {
    engage('typed');
    schedule();
  });

  const exampleButton = root.querySelector('[data-demo-example]');
  if (exampleButton) {
    exampleButton.addEventListener('click', function () {
      engage('example');
      input.value = exampleText;
      schedule();
    });
  }

  const clearButton = root.querySelector('[data-demo-clear]');
  if (clearButton) {
    clearButton.addEventListener('click', function () {
      input.value = '';
      input.focus();
      schedule();
    });
  }
}
